import React, { useState, useCallback } from 'react';
import { Save, X } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { lessonSchema, type LessonFormValues } from '../schemas/lessonSchema';
import type { SectionWithLessons, Lesson, Assessment } from '../types';
import ContentBasedFields from './ContentBasedFields';
import IltsFields from './IltsFields';

interface LessonFormModalProps {
  sections: SectionWithLessons[];
  assessments: Assessment[];
  editingLesson: Lesson | null;
  defaultSectionId: number | null;
  isSaving: boolean;
  onSave: (lessonData: Lesson, file: File | null) => Promise<void>;
  onClose: () => void;
}

const LessonFormModal: React.FC<LessonFormModalProps> = ({
  sections,
  assessments,
  editingLesson,
  defaultSectionId,
  isSaving,
  onSave,
  onClose,
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const isEditing = !!editingLesson?.id;

  const initialSectionId = editingLesson?.sectionId
    ?? defaultSectionId
    ?? sections[0]?.id
    ?? undefined;

  const initialSection = initialSectionId
    ? sections.find(s => s.id === initialSectionId)?.title ?? ''
    : '';

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<LessonFormValues>({
    resolver: zodResolver(lessonSchema),
    defaultValues: {
      title: editingLesson?.title ?? '',
      sectionId: initialSectionId,
      section: initialSection,
      lessonType: editingLesson?.lessonType ?? 'Content-Based',
      lessonDuration: editingLesson?.lessonDuration ?? '15 mins',
      description: editingLesson?.description ?? '',
      skills: editingLesson?.skills ?? [],
      contentType: editingLesson?.contentType ?? 'document',
      contentUrl: editingLesson?.contentUrl ?? '',
      assessmentId: editingLesson?.assessmentId,
      requireSectionCompletion: editingLesson?.requireSectionCompletion ?? false,
      assessmentStartDate: editingLesson?.assessmentStartDate ?? '',
      assessmentEndDate: editingLesson?.assessmentEndDate ?? '',
      iltsType: editingLesson?.iltsType ?? 'Online',
      startDate: editingLesson?.startDate ?? '',
      startTime: editingLesson?.startTime ?? '',
      endDate: editingLesson?.endDate ?? '',
      endTime: editingLesson?.endTime ?? '',
      eventVenue: editingLesson?.eventVenue ?? '',
      meetUrl: editingLesson?.meetUrl ?? '',
      lessonContentDocument: editingLesson?.lessonContentDocument,
      videoUpload: editingLesson?.videoUpload,
      scormPackage: editingLesson?.scormPackage,
    },
  });

  const lessonType = watch('lessonType');
  const contentType = watch('contentType');

  const handleLessonTypeChange = useCallback((newType: 'ILTS' | 'Content-Based') => {
    setValue('lessonType', newType);
    if (newType === 'ILTS') {
      setValue('iltsType', 'Online');
    } else {
      setValue('contentType', 'document');
    }
  }, [setValue]);

  const handleContentTypeChange = useCallback((newType: string) => {
    setValue('contentType', newType as LessonFormValues['contentType']);
    setValue('contentUrl', '');
    setValue('lessonContentDocument', undefined);
    setValue('videoUpload', undefined);
    setValue('scormPackage', undefined);
    if (newType !== 'quiz') {
      setValue('assessmentId', undefined);
      setValue('requireSectionCompletion', false);
      setValue('assessmentStartDate', '');
      setValue('assessmentEndDate', '');
    }
  }, [setValue]);

  const onSubmit = async (data: LessonFormValues) => {
    const lessonData: Lesson = {
      ...data,
      ...(isEditing ? { id: editingLesson!.id } : {}),
    };
    await onSave(lessonData, uploadedFile);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-large">
        <div className="modal-header">
          <h3>{isEditing ? 'Edit Lesson' : 'Add Lesson'}</h3>
          <button onClick={onClose} className="btn-icon">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="modal-body">
            {/* Section Selector */}
            {!isEditing && (
              <div className="form-row">
                <div className="form-group">
                  <label>Select Section *</label>
                  <Controller
                    name="sectionId"
                    control={control}
                    render={({ field }) => (
                      <select
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const sectionId = parseInt(e.target.value);
                          const section = sections.find(s => s.id === sectionId);
                          field.onChange(sectionId);
                          setValue('section', section?.title ?? '');
                        }}
                        className="form-select"
                        style={{ padding: '10px', fontSize: '14px', fontWeight: '500' }}
                      >
                        {sections.map((section, idx) => (
                          <option key={section.id} value={section.id}>
                            Section {idx + 1}: {section.title}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.sectionId && (
                    <span style={{ color: '#d32f2f', fontSize: '12px' }}>
                      {errors.sectionId.message}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Lesson Title */}
            <div className="form-row">
              <div className="form-group">
                <label>Lesson Title *</label>
                <input
                  type="text"
                  {...register('title')}
                  placeholder="Enter lesson title"
                  className="form-input"
                />
                {errors.title && (
                  <span style={{ color: '#d32f2f', fontSize: '12px' }}>{errors.title.message}</span>
                )}
              </div>
            </div>

            {/* Lesson Type & Duration */}
            <div className="form-row">
              <div className="form-group">
                <label>Lesson Type *</label>
                <select
                  value={lessonType}
                  onChange={(e) => handleLessonTypeChange(e.target.value as 'ILTS' | 'Content-Based')}
                  className="form-select"
                  style={{
                    padding: '10px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                  }}
                >
                  <option value="Content-Based">Content-Based</option>
                  <option value="ILTS">Instructor-Led Training Session (ILTS)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Duration</label>
                <input
                  type="text"
                  {...register('lessonDuration')}
                  placeholder="e.g., 30 mins"
                  className="form-input"
                />
              </div>
            </div>

            {/* Content-Based Fields */}
            {lessonType === 'Content-Based' && (
              <ContentBasedFields
                contentType={contentType}
                assessments={assessments}
                register={register}
                setValue={setValue}
                watch={watch}
                onContentTypeChange={handleContentTypeChange}
                uploadedFile={uploadedFile}
                onFileChange={setUploadedFile}
              />
            )}

            {/* ILTS Fields */}
            {lessonType === 'ILTS' && (
              <IltsFields register={register} watch={watch} setValue={setValue} />
            )}

            {/* Description */}
            <div className="form-row">
              <div className="form-group">
                <label>Description</label>
                <textarea
                  {...register('description')}
                  placeholder="Enter lesson description"
                  className="form-textarea"
                  rows={3}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="submit" disabled={isSaving} className="btn-primary">
              <Save size={18} />
              {isSaving ? 'Saving...' : 'Save Lesson'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LessonFormModal;
